/**
 * Dynamic validation utilities that work with API-defined field rules
 */

import { calculateAge, isDobWithinAgeRange } from "./formatters";
import { logWarning } from "./logger";

const getLeadDobAgeBounds = () => ({ minAge: 18, maxAge: 75 });

const getLeadDobAgeError = () => {
  const { minAge, maxAge } = getLeadDobAgeBounds();
  return `Please enter a valid date of birth between ${minAge} and ${maxAge} years.`;
};


/** Person / ID name fields (Lead dynamic form, etc.); not bank/branch/company names. */
export const NAME_FIELD_MAX_CHARS = 30;
const NAME_FIELD_LENGTH_ERROR =
  "Please reduce the name to 30 characters or fewer.";

export function isNameLikeSemanticField(fieldConfig) {
  if (!fieldConfig || typeof fieldConfig !== "object") return false;
  const ref = (
    fieldConfig.fieldReference ||
    fieldConfig.name ||
    fieldConfig.fieldName ||
    ""
  ).toLowerCase();
  if (!ref.includes("name")) return false;
  return !(
    (ref.includes("bank") && ref.includes("name")) ||
    (ref.includes("branch") && ref.includes("name")) ||
    (ref.includes("company") && ref.includes("name"))
  );
}

/** True when a person name is longer than the BizOps limit. */
export function isPersonNameOverLimit(value) {
  return String(value ?? "").length > NAME_FIELD_MAX_CHARS;
}

/**
 * Aadhaar or lead prefill stays read-only when within 30 characters.
 * Pass keepUnlocked=true after the value was ever over the limit so the user can
 * keep deleting/editing instead of freezing after the first keystroke.
 */
export function isAutofilledNameLocked(isAutofilled, value, keepUnlocked = false) {
  if (!isAutofilled) return false;
  if (keepUnlocked) return false;
  const text = String(value ?? "");
  if (!text.trim()) return false;
  return text.length <= NAME_FIELD_MAX_CHARS;
}

/** Generous input cap while shortening an over-limit autofilled name (do not use value.length). */
export const NAME_FIELD_OVER_LIMIT_EDIT_MAX = 100;

/**
 * Keep letters and spaces. Values already over maxLen stay intact so the user can shorten them.
 * When previousValue is over the BizOps limit, the result cannot grow longer than previousValue.
 */
export function cleanPersonNamePreservingOverLimit(
  value,
  maxLen = NAME_FIELD_MAX_CHARS,
  previousValue,
) {
  const cleaned = String(value ?? "")
    .replace(/[^A-Za-z\s]/g, "")
    .replace(/\s+/g, " ")
    .trimStart();
  const previous = String(previousValue ?? "");
  let next =
    cleaned.length > maxLen ? cleaned : cleaned.slice(0, maxLen);
  // Allow free shortening, but never grow an already over-limit autofill.
  if (previous.length > maxLen && next.length > previous.length) {
    next = next.slice(0, previous.length);
  }
  return next;
}

/**
 * While over the limit, allow a large maxLength so the user can keep deleting.
 * Using value.length as maxLength freezes the field after one keystroke.
 */
export function personNameInputMaxLength(
  value,
  withinLimitMax = NAME_FIELD_MAX_CHARS,
) {
  const text = String(value ?? "");
  return text.length > NAME_FIELD_MAX_CHARS
    ? NAME_FIELD_OVER_LIMIT_EDIT_MAX
    : withinLimitMax;
}

const isEmpty = (value) => 
  value === null || value === undefined || value === '' || 
  (typeof value === 'string' && value.trim() === '') ||
  (Array.isArray(value) && value.length === 0);

/** Linear-time email check (avoids ReDoS-prone regex). */
const isValidEmailAddress = (value) => {
  if (!value || typeof value !== 'string') return false;
  const email = value.trim();
  if (email.length < 3 || email.length > 254) return false;
  if (email.includes(' ') || email.includes('..')) return false;

  const at = email.indexOf('@');
  if (at <= 0 || at !== email.lastIndexOf('@')) return false;

  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  if (!local || local.startsWith('.') || local.endsWith('.')) return false;
  if (
    !domain ||
    domain.startsWith('.') ||
    domain.endsWith('.') ||
    domain.startsWith('-') ||
    domain.endsWith('-')
  ) {
    return false;
  }

  const labels = domain.split('.');
  if (labels.length < 2) return false;

  const validLabel = /^[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?$/;
  if (!labels.every((label) => validLabel.test(label))) return false;

  const tld = labels[labels.length - 1];
  if (tld.length < 2 || !/^[A-Za-z]+$/.test(tld)) return false;

  const validLocal = /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+$/;
  return validLocal.test(local);
};

const validateRegex = (value, validation, label, name) => {
  if (!validation?.regex) return null;
  try {
    const pattern = String(validation.regex).replace(/(^["']|["']$)/g, '');
    if (!new RegExp(pattern).test(value)) {
      return validation.errorMessage !== validation.requiredErrorMessage 
        ? validation.errorMessage 
        : validation.message || `${label || name} is invalid.`;
    }
  } catch (e) {
    logWarning('Invalid regex pattern:', validation.regex, e);
  }
  return null;
};

const validateExactLength = (value, validation, label, name, fieldConfig) => {
  if (validation?.exactLength === undefined) return null;
  // Skip validation if field is empty (required validation will handle it)
  if (isEmpty(value)) return null;
  
  let valueToValidate = String(value || '');
  
  // For pincode fields, extract just the numeric part if format is "pincode / city"
  const fieldName = (fieldConfig?.name || fieldConfig?.fieldName || fieldConfig?.fieldReference || '').toLowerCase();
  const isPincodeField = fieldName.includes('pincode') || fieldName.includes('pin_code') || fieldName.includes('postal');
  
  if (isPincodeField && valueToValidate.includes(' / ')) {
    // Extract pincode part before " / "
    valueToValidate = valueToValidate.split(' / ')[0].trim();
  }
  
  const digitsOnly = valueToValidate.replace(/\D/g, '');
  if (digitsOnly.length !== validation.exactLength) {
    return validation.errorMessage !== validation.requiredErrorMessage
      ? validation.errorMessage
      : `${label || name} must be exactly ${validation.exactLength} digits.`;
  }
  return null;
};

const validateLength = (value, validation, label, name, fieldConfig) => {
  if (typeof value !== 'string') return null;
  // Skip validation if field is empty (required validation will handle it)
  if (isEmpty(value)) return null;
  
  let valueToValidate = value;
  
  // For pincode fields, extract just the numeric part if format is "pincode / city"
  const fieldName = (fieldConfig?.name || fieldConfig?.fieldName || fieldConfig?.fieldReference || '').toLowerCase();
  const isPincodeField = fieldName.includes('pincode') || fieldName.includes('pin_code') || fieldName.includes('postal');
  
  if (isPincodeField && valueToValidate.includes(' / ')) {
    // Extract pincode part before " / "
    valueToValidate = valueToValidate.split(' / ')[0].trim();
  }
  
  const msg = validation.errorMessage || validation.message;
  if (validation.minLength && valueToValidate.length < validation.minLength) {
    return msg || `${label || name} must be at least ${validation.minLength} characters.`;
  }
  if (validation.maxLength && valueToValidate.length > validation.maxLength) {
    return msg || `${label || name} must not exceed ${validation.maxLength} characters.`;
  }
  return null;
};

const validateNumericRange = (value, validation, label, name) => {
  if (value === undefined || value === null || value === "") return null;
  const numericValue = Number(String(value).replace(/,/g, ""));
  if (isNaN(numericValue)) return null;
  let min = (name==="Text45649336" && !validation.min) ? 250000 : validation.min;
  let max = (name==="Text45649336" && !validation.max) ? 100000000 : validation.max;

  if (min !== undefined && numericValue < min) {
    return `${label || name} must be at least ${min}.`;
  }
  if (max !== undefined && numericValue > max) {
    return `${label || name} must not exceed ${max}.`;
  }
  return null;
};

const validateEmail = (value, validation, fieldType) => {
  // Skip validation if field is empty (required validation will handle it)
  if (isEmpty(value)) return null;
  if (fieldType.fieldReference !== "emailAddress") return null;
  
  if (!isValidEmailAddress(value)) {
    return (
      // validation?.errorMessage ||
      // validation?.message ||
      "Please enter a valid email address."
    );
  }
  return null;
};

// Note: validatePhone is redundant if using exactLength validation for mobile numbers
const validatePhone = (value, validation, fieldType) => {
  // Skip validation if field is empty (required validation will handle it)
  if (isEmpty(value)) return null;
  if ((validation?.type !== 'phone' && validation?.type !== 'mobile' && fieldType !== 'tel') ||
      (/^[\d\s\-+()]+$/.test(value) && value.replace(/\D/g, '').length >= 10)) return null;
  return validation.errorMessage || validation.message || 'Please enter a valid phone number.';
};

const validateCustom = (value, validation) => 
  typeof validation?.custom === 'function' ? validation.custom(value) || null : null;

/**
 * Custom validation for mobile number (bypasses form builder validation)
 */
const validateMobileCustom = (value) => {
  if (isEmpty(value)) return null; // Required validation will handle empty
  
  // Ensure value is string and extract digits only
  const digitsOnly = String(value || '').replace(/\D/g, '');
  
  // Must be exactly 10 digits
  if (digitsOnly.length !== 10) {
    return 'Enter 10 digit mobile number';
  }
  
  // Check if it starts with valid Indian mobile prefix (6-9)
  if (!/^[6-9]/.test(digitsOnly)) {
    return 'Mobile number must start with 6, 7, 8, or 9';
  }
  
  return null;
};

/**
 * Custom validation for date of birth (bypasses form builder validation)
 */
const validateDOBCustom = (value, fieldConfig) => {
  if (isEmpty(value)) return null; // Required validation will handle empty

  if (typeof value !== "string" || value.length === 0) {
    return "Please enter a valid date";
  }

  const raw = String(value).trim();
  const isoRegex = /^\d{4}-\d{2}-\d{2}$/; // yyyy-mm-dd (native date input)
  const dmyRegex = /^\d{2}-\d{2}-\d{4}$/; // dd-mm-yyyy (manual typed fallback)

  if (!isoRegex.test(raw) && !dmyRegex.test(raw)) {
    return "Please enter a valid date";
  }

  const isoDob = dmyRegex.test(raw)
    ? `${raw.slice(6, 10)}-${raw.slice(3, 5)}-${raw.slice(0, 2)}`
    : raw;

  const [yy, mm, dd] = isoDob.split("-").map((part) => Number(part));
  const date = new Date(yy, mm - 1, dd);
  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== yy ||
    date.getMonth() + 1 !== mm ||
    date.getDate() !== dd
  ) {
    return "Please enter a valid date";
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  if (date > today) {
    return "Date of birth cannot be in the future";
  }

  const age = calculateAge(isoDob);
  if (age === null) {
    return "Please enter a valid date";
  }

  const minAgeOverride = fieldConfig?.validation?.dobMinAge;
  const maxAgeOverride = fieldConfig?.validation?.dobMaxAge;
  const hasMinOverride = Number.isFinite(minAgeOverride);
  const hasMaxOverride = Number.isFinite(maxAgeOverride);

  if (hasMinOverride || hasMaxOverride) {
    const inRange = isDobWithinAgeRange(isoDob, {
      minAge: hasMinOverride ? Number(minAgeOverride) : undefined,
      maxAge: hasMaxOverride ? Number(maxAgeOverride) : undefined,
    });
    if (!inRange) {
      if (hasMinOverride && hasMaxOverride) {
        return `Please enter a valid date of birth between ${Number(minAgeOverride)} and ${Number(maxAgeOverride)} years.`;
      }
      if (hasMinOverride) {
        return `Minimum age should be ${Number(minAgeOverride)} years.`;
      }
      return `Maximum age should be ${Number(maxAgeOverride)} years.`;
    }
  }

  // Default guard for lead-like DOB fields when no explicit override is provided.
  // (validateDOBCustom only runs for fields isDOBField() already matched, so no
  // extra "is this really a DOB field" check is needed here — see call site below.)
  if (!hasMinOverride && !hasMaxOverride) {
    const { minAge, maxAge } = getLeadDobAgeBounds();
    if (!isDobWithinAgeRange(isoDob, { minAge, maxAge })) {
      return getLeadDobAgeError();
    }
  }

  return null;
};

/**
 * Check if field is mobile number field
 */
const isMobileField = (fieldConfig) => {
  const fieldName = (fieldConfig.name || fieldConfig.fieldName || '').toLowerCase();
  const fieldType = (fieldConfig.type || '').toLowerCase();
  const mobileKeywords = ['mobile', 'phone', 'mobilenumber', 'mobile_number'];
  const mobileTypes = ['tel', 'phone', 'mobile'];
  return mobileTypes.includes(fieldType) || 
         mobileKeywords.some(keyword => fieldName.includes(keyword));
};

/**
 * True for date-of-birth semantics (Lead and elsewhere). Not every `type: date` field.
 */
export const isDateOfBirthField = (fieldConfig) => {
  if (!fieldConfig) return false;
  const ref = (
    fieldConfig.fieldReference ||
    fieldConfig.name ||
    fieldConfig.fieldName ||
    ""
  ).toLowerCase();
  const dobKeywords = [
    "dob",
    "dateofbirth",
    "date_of_birth",
    "birthdate",
    "birth_date",
    "birthday",
  ];
  return dobKeywords.some((keyword) => ref.includes(keyword));
};

const isDOBField = isDateOfBirthField;

export const validateField = (value, fieldConfig) => {
  if (!fieldConfig) return null;
  
  const { validation, label, name } = fieldConfig;
  const fieldName = name || fieldConfig.fieldName || fieldConfig.fieldReference;
  
  // Handle required validation first
  const isRequired = validation?.required ?? fieldConfig.required ?? false;
  if (isRequired && isEmpty(value)) {
    return validation?.requiredErrorMessage || `${label || fieldName || 'This field'} is required.`;
  }
  
  // If empty and not required, skip other validations
  if (isEmpty(value)) return null;

  if (
    typeof value === "string" &&
    isNameLikeSemanticField(fieldConfig) &&
    value.length > NAME_FIELD_MAX_CHARS
  ) {
    return NAME_FIELD_LENGTH_ERROR;
  }

  // Use custom validation for mobile and DOB fields (bypass form builder validation)
  if (isMobileField(fieldConfig)) {
    return validateMobileCustom(value);
  }
  
  if (isDOBField(fieldConfig)) {
    return validateDOBCustom(value, fieldConfig);
  }

  // For other fields, use form builder validation
  if (!validation) return null;

  const validations = [
    () => validateExactLength(value, validation, label, name, fieldConfig),
    () => validateRegex(value, validation, label, name),
    () => validateLength(value, validation, label, name, fieldConfig),
    () => validateNumericRange(value, validation, label, name),
    () => validateEmail(value, validation, fieldConfig),
    () => validatePhone(value, validation, fieldConfig.type),
    () => validateCustom(value, validation),
  ];

  for (const fn of validations) {
    const error = fn();
    if (error) return error;
  }
  return null;
};

const REQUIRED_BOOLEAN_FIELDS = [
  "politicallyExposed",
];

export const validateForm = (formData, fields) => {
  const errors = {};
  fields.forEach(field => {
    // Prefer fieldReference (semantic name) over DDM field name for consistency
    const fieldName = field.fieldReference || field.name || field.fieldName;
    const value = formData[fieldName];
       // Required boolean fields
    if (REQUIRED_BOOLEAN_FIELDS.includes(fieldName)) {
      // Valid values are only true or false
      if (value !== true && value !== false) {
        errors[fieldName] = "Please make a selection before proceeding";
      }

      return;
    }

    const error = validateField(formData[fieldName], field);
    const nameLower = fieldName?.toLowerCase() || "";
    // Bank Account Proof is NOT required when
    // "Use the same bank details as the Proposer" = Yes
    const samePaBankAsProposer =
      formData.samePaBankAsProposer === true ||
      String(formData.samePaBankAsProposer).trim().toLowerCase() === "true" ||
      String(formData.samePaBankAsProposer).trim().toLowerCase() === "yes";

    if (
      nameLower === "pabankaccountproof" &&
      samePaBankAsProposer
    ) {
      return;
    }

    const sameSaBankAsProposer =
      formData.sameSaBankAsProposer === true ||
      String(formData.sameSaBankAsProposer).trim().toLowerCase() === "true" ||
      String(formData.sameSaBankAsProposer).trim().toLowerCase() === "yes";

    if (
      nameLower === "sabankaccountproof" &&
      sameSaBankAsProposer
    ) {
      return;
    }

    if (error) errors[fieldName] = error;
    

const isMobileField =
      nameLower.includes("mobile") ||
      nameLower.includes("phone") ||
      nameLower.includes("tel");

    if (
      isMobileField &&
      value &&
      !/^[6-9]\d{9}$/.test(value) && // strict Indian mobile validation
      !errors[fieldName]
    ) {
      errors[fieldName] = "Please enter a valid mobile number";
    }

const isPincodeField =
  nameLower.includes("pin") ||
  nameLower.includes("pincode") ||
  nameLower.includes("postal") ||
  nameLower.includes("zip");

// Pincode UI can show "201001 / Ghaziabad"; validate 6 digits from pin part only
if (isPincodeField && value && !errors[fieldName]) {
  let pinPart = String(value).trim();
  if (pinPart.includes(" / ")) {
    pinPart = pinPart.split(" / ")[0].trim();
  }
  const pinDigits = pinPart.replace(/\D/g, "");
  if (pinDigits.length !== 6) {
    errors[fieldName] = "Enter valid 6 digit pincode";
  }
}

 const isEmailField = nameLower.includes("email");

    if (
      isEmailField &&
      value &&
      !isValidEmailAddress(value) &&
      !errors[fieldName]
    ) {
      errors[fieldName] = "Please enter a valid email address";
    }


    const isPanField = nameLower.includes("pan");

    if (
      isPanField &&
      value &&
      !/^[A-Z]{3}P[A-Z][0-9]{4}[A-Z]$/.test(value.toUpperCase()) &&
      !errors[fieldName]
    ) {
      errors[fieldName] = "Please enter a valid PAN number";
    }

  });
  return errors;
};

export const validateFieldByName = (fieldName, value, fields) => {
  if (!fieldName) return null;
  
  // Find field by exact name match using the same extraction logic as extractFieldMetadata
  const field = fields.find(f => {
    const fName = f.name || f.fieldName || f.fieldReference;
    // Use strict equality to ensure exact match
    return fName === fieldName;
  });
  
  // Only validate if we found the exact field match
  if (!field) {
    // Silently return null - field might not exist in current step
    return null;
  }
  
  // Validate the field with its value
  return validateField(value, field);
};
